import {
  TableContainer,
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
} from '@material-ui/core'
import React, { Fragment } from 'react'
import { useReactiveVar } from '@apollo/client'
import { ProjectFragment } from '../../../generated/graphql'
import { FullFormatOptions } from '../../../util/formatters'
import { BreakdownTableRow } from './BreakdownTableRow'
import { ResultsTableHeaderRow } from '../../common/ResultsTableHeaderRow'
import { useTableStyles } from '../../common/use-table-styles'
import { SelectedVersion } from '../../layout/VersionsSelector'

import {
  checkIfVersionHasBuilding,
  EmissionRow,
  GroupedEmission,
  groupEmissions,
  getVersionValue,
  TableType,
  EmissionWithVersionId,
  getPrefillData,
  sortGroupedEmissions,
} from './carbon-footprint-table-util'
import { dictionariesVar } from '../../../apollo/reactive-vars'

type Props = {
  project: ProjectFragment
  selectedVersions: SelectedVersion[]
  tableType: TableType
  formatter: (value: number, options: FullFormatOptions) => string
  unit: string
  createPrefilledEmission?: (
    emission: EmissionWithVersionId,
    newEmissionVersionId: string,
    buildingName: string,
  ) => void
  updateEmission?: (emission: EmissionWithVersionId) => void
}

export default function FixedEmissionsTable({
  project,
  selectedVersions,
  tableType,
  formatter,
  unit,
  createPrefilledEmission,
  updateEmission,
}: Props): React.ReactElement {
  const classes = useTableStyles()
  const { versionIdsToBuildingNames } = useReactiveVar(dictionariesVar)

  const selectedVersionIds = selectedVersions.map((v) => v.id)
  const projectWithSelectedVersionsOnly = {
    ...project,
    versions: project.versions.filter((version) =>
      selectedVersionIds.includes(version.id),
    ),
  }
  const groupedEmissions = groupEmissions(
    projectWithSelectedVersionsOnly,
    tableType,
  )

  const mapValues = (row: EmissionRow, buildingName: string) =>
    selectedVersions.map((version) => {
      const buildingExists = checkIfVersionHasBuilding(
        version.id,
        versionIdsToBuildingNames,
        buildingName,
      )
      return {
        versionId: version.id,
        value: getVersionValue(version, row, formatter, buildingExists),
      }
    })

  const handleEmissionClick = (
    versionId: string,
    row: EmissionRow,
    buildingName: string,
  ) => {
    const emission = row.emissions.find((e) => e.versionId === versionId)
    if (emission && updateEmission) {
      updateEmission(emission)
    } else if (createPrefilledEmission) {
      createPrefilledEmission(
        getPrefillData(selectedVersions, row),
        versionId,
        buildingName,
      )
    }
  }

  const sortedGroupedEmissions = sortGroupedEmissions(groupedEmissions)

  return (
    <TableContainer component={Paper}>
      <Table className={classes.table}>
        <TableHead>
          <ResultsTableHeaderRow
            title=""
            values={selectedVersions.map((v) => v.name)}
          />
        </TableHead>
        <TableBody>
          {sortedGroupedEmissions.map(
            (group: GroupedEmission) =>
              group.rows.length > 0 && (
                <Fragment key={group.buildingName}>
                  <TableRow>
                    <TableCell
                      colSpan={selectedVersions.length + 1}
                      className={classes.titleHeaderCell}
                    >
                      {group.buildingName}
                    </TableCell>
                  </TableRow>
                  {group.rows.map((row) => (
                    <BreakdownTableRow
                      key={`${group.buildingName}-${row.emissionType}`}
                      unit={unit}
                      versionValues={mapValues(row, group.buildingName)}
                      row={row}
                      buildingName={group.buildingName}
                      handleEmissionClick={handleEmissionClick}
                    />
                  ))}
                </Fragment>
              ),
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
